import React, { useEffect, useState } from "react";
import axios from "axios";
import { Download, Lock } from "lucide-react";
import { useAuth, API } from "../App";
import { CATEGORY_DISPLAY_NAMES } from "../utils/categoryUtils";


const FY_OPTIONS = [
  { label: "FY 2025–2026", value: 2026 },
  { label: "FY 2024–2025", value: 2025 },
  { label: "FY 2023–2024", value: 2024 },
];

const RATIONALE = {
  // Deductible
  "Vehicle & Transport":    "Deductible for work-related travel only. Commuting to your regular workplace is not claimable. Keep a logbook if using the actual cost method.",
  "Phone & Internet":       "Only the work-related percentage is deductible. You must apportion personal use — the ATO expects a reasonable estimate.",
  "Home Office":            "Deductible using the ATO fixed-rate method (67¢/hr) or actual cost method. Occupancy costs (rent, mortgage interest) can only be claimed if your home is a place of business.",
  "Bank Fees":              "Fees on accounts used to earn investment or business income are deductible. Fees on personal transaction accounts are not.",
  "Education & Training":   "Deductible if the course maintains or improves skills in your current job. Courses to get a new job or change careers are not claimable.",
  "Donations":              "Only donations to ATO-registered deductible gift recipients (DGR) are claimable. Membership fees, raffle tickets, and items purchased at charity events are not.",
  "Rental Expenses":        "Deductible against rental income — includes property management fees, council rates, insurance, repairs, and depreciation. Capital improvements are not immediately deductible.",
  // Non-deductible
  "Groceries":              "Personal living expenses are not tax deductible for individuals.",
  "Dining & Takeaway":      "Personal meals are not deductible. Business meals with clients have strict substantiation requirements — consult your accountant.",
  "Health & Medical":       "The net medical expenses tax offset was abolished in 2019. Health insurance premiums are not deductible for individuals (private health rebate applies separately).",
  "Insurance":              "Personal insurance premiums (home, car, health) are not deductible. Income protection insurance premiums may be deductible — confirm with your accountant.",
  "Shopping & Retail":      "Personal purchases are not tax deductible.",
  "Subscriptions":          "Personal streaming and entertainment subscriptions are not deductible. Work-related software subscriptions may be — review individually.",
  "Buy Now Pay Later":      "Not a deduction category. The deductibility of each underlying purchase depends on its nature.",
  "Entertainment":          "Personal entertainment is not deductible. Business entertainment has strict ATO rules and limited deductibility.",
  "Personal Transport":     "Rideshare, taxi, and personal travel costs are not deductible.",
  "Utilities":              "Household utilities are generally not deductible. A portion may be claimable under the home office rules if applicable.",
  "Transfer":               "Internal transfers between your own accounts are not income or expenses — no tax impact.",
  "Internal Transfer":      "Internal transfers between your own accounts are not income or expenses — no tax impact.",
  "Income":                 "Assessable income — included in your taxable income and declared in your tax return.",
  "Investment Income":      "Assessable income. Dividends may include franking credits which reduce your tax payable.",
  "Needs Review":           "These transactions have not been categorised yet. Review each one and assign the correct category before lodging.",
};


export default function Reports() {
  const { user } = useAuth();
  const [fy, setFy] = useState(2026);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState("overview");
  const [exporting, setExporting] = useState(false);

  const isPro = user?.plan === "pro";

  useEffect(() => {
    setLoading(true);
    setError(null);
    const token = localStorage.getItem("taxbuddy_token");
    axios.get(`${API}/api/reports/summary?financial_year=${fy}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => setData(res.data))
      .catch(() => setError("Could not load report. Please try again."))
      .finally(() => setLoading(false));
  }, [fy]);

  const handleExport = async () => {
    if (!isPro) return;
    setExporting(true);
    try {
      const token = localStorage.getItem("taxbuddy_token");
      const res = await axios.get(`${API}/api/export/excel`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `TaxBuddy_FY${fy}.xlsx`;
      a.click();
    } catch {
      alert("Export failed. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  const fmt = (n) => `$${(n || 0).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tax Reports</h1>
          <p className="text-sm text-gray-500 mt-0.5">Your financial year summary</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={fy}
            onChange={e => setFy(Number(e.target.value))}
            className="text-sm border border-gray-200 rounded-xl px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-green-500"
          >
            {FY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <button
            onClick={handleExport}
            disabled={!isPro || exporting}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition ${
              isPro
                ? "text-white hover:opacity-90"
                : "bg-gray-100 text-gray-400 cursor-not-allowed"
            }`}
            style={isPro ? { backgroundColor: "#1A4D2E" } : {}}
          >
            {isPro ? <Download size={15} /> : <Lock size={15} />}
            {exporting ? "Exporting…" : "Export Excel"}
          </button>
        </div>
      </div>

      {!isPro && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-4 flex items-center justify-between gap-4">
          <div>
            <p className="font-semibold text-amber-900 text-sm">Upgrade to Pro to export</p>
            <p className="text-xs text-amber-700 mt-0.5">Get the accountant-ready Excel workbook with all transactions, deductions, and income summary.</p>
          </div>
          <a href="/pricing" className="text-xs font-bold text-white px-4 py-2 rounded-xl flex-shrink-0" style={{ backgroundColor: "#1A4D2E" }}>
            See Pricing
          </a>
        </div>
      )}

      {loading && (
        <div className="text-center py-16 text-gray-400 text-sm">Loading report…</div>
      )}

      {error && (
        <div className="text-center py-16 text-red-400 text-sm">{error}</div>
      )}

      {!loading && !error && data && (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: "Total Income", value: fmt(data.total_income), color: "text-green-700", bg: "bg-green-50", sub: `${data.income_count || 0} transactions` },
              { label: "Total Expenses", value: fmt(data.total_expenses), color: "text-red-600", bg: "bg-red-50", sub: `${data.expense_count || 0} transactions` },
              { label: "Potential Deductions", value: fmt(data.total_deductions), color: "text-blue-700", bg: "bg-blue-50", sub: "Confirm with accountant" },
              { label: "Net Position", value: fmt(data.net_position), color: data.net_position >= 0 ? "text-green-700" : "text-red-600", bg: "bg-gray-50", sub: "" },
            ].map(({ label, value, color, bg, sub }) => (
              <div key={label} className={`${bg} rounded-2xl p-5`}>
                <p className={`text-2xl font-bold ${color}`}>{value}</p>
                <p className="text-sm text-gray-600 mt-1 font-medium">{label}</p>
                {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
              </div>
            ))}
          </div>

          {/* Tabs */}
          <div className="flex gap-1 bg-gray-100 rounded-xl p-1 w-fit">
            {["overview","deductions","income","expenses"].map(t => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`px-4 py-1.5 rounded-lg text-sm font-medium transition capitalize ${
                  tab === t ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {t}
              </button>
            ))}
          </div>

          {/* Tab content */}
          {tab === "overview" && (
            <CategoryTable
              title="All Categories"
              items={Object.entries(data.by_category || {})
                .sort((a, b) => b[1].total - a[1].total)
                .map(([cat, v]) => ({ name: CATEGORY_DISPLAY_NAMES[cat] || cat.replace(/_/g, " "), count: v.count, total: v.total, deductible: v.deductible }))}
            />
          )}

          {tab === "deductions" && (
            data.total_deductions > 0 ? (
              <CategoryTable
                title="Tax Deductions"
                items={Object.entries(data.by_category || {})
                  .filter(([, v]) => v.deductible)
                  .sort((a, b) => b[1].total - a[1].total)
                  .map(([cat, v]) => ({ name: cat, count: v.count, total: v.total, deductible: true }))}
              />
            ) : (
              <div className="text-center py-12 text-gray-400 text-sm bg-gray-50 rounded-2xl">
                No deductible expenses found for this financial year
              </div>
            )
          )}

          {tab === "income" && (
            <TransactionList title="Income" items={data.income_items || []} />
          )}

          {tab === "expenses" && (
            <CategoryTable
              title="Expenses by Category"
              items={Object.entries(data.by_category || {})
                .filter(([cat]) => cat !== "Transfer" && cat !== "Income" && cat !== "Investment Income")
                .sort((a, b) => b[1].total - a[1].total)
                .map(([cat, v]) => ({ name: CATEGORY_DISPLAY_NAMES[cat] || cat.replace(/_/g, " "), count: v.count, total: v.total, deductible: v.deductible }))}
            />
          )}
        </>
      )}
    </div>
  );
}

function CategoryTable({ title, items }) {
  const fmt = (n) => `$${(n || 0).toLocaleString("en-AU", { minimumFractionDigits: 2 })}`;
  const total = items.reduce((s, i) => s + i.total, 0);
  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-50">
        <p className="font-semibold text-gray-900">{title}</p>
        <p className="text-sm font-bold text-gray-900">{fmt(total)} total</p>
      </div>
      {items.length === 0 ? (
        <p className="text-center text-gray-400 text-sm py-10">No data for this period</p>
      ) : (
        <div className="divide-y divide-gray-50">
          {items.map(item => (
            <div key={item.name} className="px-6 py-3">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-medium text-gray-900">{item.name}</p>
                      {item.deductible && (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium flex-shrink-0">Deductible</span>
                      )}
                    </div>
                    <p className="text-xs text-gray-400 mt-0.5">{item.count} transactions</p>
                    {RATIONALE[item.name] && (
                      <p className="text-xs text-gray-500 mt-1 leading-relaxed max-w-xl">{RATIONALE[item.name]}</p>
                    )}
                  </div>
                </div>
                <p className="text-sm font-semibold text-gray-900 flex-shrink-0">{fmt(item.total)}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TransactionList({ title, items }) {
  const fmt = (n) => `$${(n || 0).toLocaleString("en-AU", { minimumFractionDigits: 2 })}`;
  const safeItems = Array.isArray(items) ? items : [];
  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-50">
        <p className="font-semibold text-gray-900">{title}</p>
      </div>
      {safeItems.length === 0 ? (
        <p className="text-center text-gray-400 text-sm py-10">No income recorded for this period</p>
      ) : (
        <div className="divide-y divide-gray-50">
          {safeItems.map((t, i) => (
            <div key={i} className="flex items-center justify-between px-6 py-3">
              <div>
                <p className="text-sm font-medium text-gray-900">{t.description}</p>
                <p className="text-xs text-gray-400">{t.date} · {t.category}</p>
              </div>
              <p className="text-sm font-semibold text-green-700">{fmt(t.amount)}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
